import { LatLngBounds, LatLngExpression, LatLngTuple } from 'leaflet';
import { DuplicateAnalysis, DuplicateGateEntry, DuplicateGroup, MergeResult } from '../types/Gate';
import { plural } from './plural';

/**
 * The Duplicates view on Route Management. The server finds groups of gates
 * whose start and end markers both sit within a few metres of each other, and
 * names the gate a merge of each group keeps. These helpers turn that into
 * what the page says: which group a gate is in, what to call a group, what a
 * merge will do, and where on the map the group sits.
 */

/** Two gates are duplicates when both markers are within this of each other. */
export const DUPLICATE_GATE_THRESHOLD_M = 10;

/** Where a gate sits in the duplicate groups: its group's index and size. */
export interface DuplicateMembership {
  groupIndex: number;
  size: number;
}

export function indexDuplicateGroups(
  analysis: DuplicateAnalysis | null | undefined,
): Map<string, DuplicateMembership> {
  const index = new Map<string, DuplicateMembership>();
  analysis?.duplicate_groups.forEach((group, groupIndex) => {
    for (const gate of group.gates) index.set(gate.id, { groupIndex, size: group.gate_count });
  });
  return index;
}

/** The gate a merge of the group keeps, as the server decided it. */
export function keptGate(group: DuplicateGroup): DuplicateGateEntry {
  return group.gates.find((gate) => gate.id === group.canonical_gate_id) ?? group.gates[0];
}

/** What to call a gate in a group: its name, else its route's name, else the address. */
export function duplicateGateLabel(gate: DuplicateGateEntry): string {
  return gate.name || gate.route_description || gate.address || `Gate ${gate.id.slice(0, 8)}...`;
}

export interface DuplicateGroupSummary {
  /** The kept gate's label. */
  title: string;
  /** Route name and address, when they add something to the title. */
  place: string;
  /** How many gates, and how close they are. */
  detail: string;
}

export function describeDuplicateGroup(group: DuplicateGroup): DuplicateGroupSummary {
  const kept = keptGate(group);
  const title = duplicateGateLabel(kept);
  const place = [kept.route_description, kept.address]
    .filter((part, i, parts) => part && part !== title && parts.indexOf(part) === i)
    .join(' · ');
  const detail = group.max_start_delta_m === 0 && group.max_end_delta_m === 0
    ? `${plural(group.gate_count, 'gate')} at the same coordinates`
    : `${plural(group.gate_count, 'gate')}, start markers within ${group.max_start_delta_m} m` +
      ` and end markers within ${group.max_end_delta_m} m`;
  return { title, place, detail };
}

/**
 * A session's date for display. The routes endpoint sends a plain day
 * ("2026-07-13"), which is read as local midnight so the day never shifts
 * with the viewer's timezone; the duplicate analysis sends a full timestamp.
 * Anything unreadable is shown as sent rather than as "Invalid Date".
 */
export function formatSessionDate(date: string | null | undefined): string {
  if (!date) return '';
  const parsed = /^\d{4}-\d{2}-\d{2}$/.test(date) ? new Date(`${date}T00:00:00`) : new Date(date);
  return Number.isNaN(parsed.getTime()) ? date : parsed.toLocaleDateString();
}

/** The confirm text for merging a group: which gate stays, and what happens to the rest. */
export function describeGateMerge(group: DuplicateGroup): string {
  const kept = keptGate(group);
  const others = group.gates.filter((gate) => gate.id !== kept.id);
  const moving = others.reduce((n, gate) => n + gate.session_count, 0);
  const name = duplicateGateLabel(kept);
  const theOthers = `The other ${plural(others.length, 'gate')} ${others.length === 1 ? 'is' : 'are'} deleted`;
  const lines = [
    `Merge ${group.gate_count} gates into "${name}"?`,
    `"${name}" stays (${plural(kept.session_count, 'session')}).`,
    moving > 0
      ? `${theOthers} and ${others.length === 1 ? 'its' : 'their'} ${plural(moving, 'session')}` +
        ` ${moving === 1 ? 'moves' : 'move'} to it.`
      : `${theOthers}; no sessions move.`,
  ];
  return lines.join('\n');
}

/** What a gate merge did, for the notice afterwards. */
export function describeGateMergeResult(group: DuplicateGroup, result: MergeResult): string {
  const kept = group.gates.find((gate) => gate.id === result.canonical_gate_id) ?? keptGate(group);
  const moved = group.gates.filter((gate) => gate.id !== kept.id).reduce((n, gate) => n + gate.session_count, 0);
  const parts = [`${plural(result.deleted_gate_ids.length, 'gate')} deleted`];
  if (moved > 0) parts.push(`${plural(moved, 'session')} moved to it`);
  if (result.orphaned_routes_deleted > 0) {
    parts.push(`${plural(result.orphaned_routes_deleted, 'route')} left empty and removed`);
  }
  return `Merged ${plural(group.gate_count, 'gate')} into "${duplicateGateLabel(kept)}". ${parts.join(', ')}.`;
}

/** The map area covering every marker of every gate in the group. */
export function duplicateGroupBounds(group: DuplicateGroup): LatLngBounds | null {
  const points: LatLngExpression[] = [];
  for (const gate of group.gates) {
    gate.gates_lat.forEach((lat, i) => {
      const lon = gate.gates_lon[i];
      if (typeof lat === 'number' && typeof lon === 'number') points.push([lat, lon]);
    });
  }
  return points.length ? new LatLngBounds(points) : null;
}

/**
 * Where a group sits on the map: the mean of its gates' start markers, and of
 * their end markers. Rings go there, not at the centre of the group's bounds,
 * which for a long gate is halfway along it with no marker near.
 */
export function duplicateGroupCentres(group: DuplicateGroup): { start: LatLngTuple | null; end: LatLngTuple | null } {
  const mean = (index: number): LatLngTuple | null => {
    const points: LatLngTuple[] = [];
    for (const gate of group.gates) {
      const lat = gate.gates_lat[index];
      const lon = gate.gates_lon[index];
      if (typeof lat === 'number' && typeof lon === 'number') points.push([lat, lon]);
    }
    if (!points.length) return null;
    return [
      points.reduce((sum, [lat]) => sum + lat, 0) / points.length,
      points.reduce((sum, [, lon]) => sum + lon, 0) / points.length,
    ];
  };
  return { start: mean(0), end: mean(1) };
}
